/**
 * SlateCard — single "A4 whiteboard slate" tile for the Vision Board.
 *
 * Design ref: /app/design_guidelines.md §3 slate component anatomy.
 *
 * Layers (per design):
 *   1. Identity colour: 8px left edge strip + small pin dot in header
 *   2. Status border/glow: thin ring around the slate that changes with state
 *   3. Cluster chip: small pill in header showing semantic cluster (e.g. "Emergency comms")
 *   4. Single lane: "What to do next" — up to 3 bullets (fix64; consolidated
 *      from the previous waiting_on / next_steps split per Owner request)
 *   5. Footer: relative-time + Open-details button
 *
 * Edit mode (per design §5 + fix64):
 *   - Popover colour picker on the pin dot when Edit mode is on
 *   - Select dropdown for status override when Edit mode is on
 *   - Drag handle on the slate header (HTML5 native drag-and-drop)
 *
 * Cluster highlight (fix64):
 *   - Slates carry an optional `cluster_id` from the backend (e.g.
 *     emergency_comms covers EC, CC, Test Call, SIP Server).
 *   - On parent hover over any slate in a cluster, sibling slates in the
 *     same cluster stay opaque; non-cluster slates dim to ~40% opacity.
 *   - Dim treatment is controlled by the `dimmed` prop (parent owns state).
 */

import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import {
  CheckCircle2,
  Clock3,
  AlertOctagon,
  PauseCircle,
  ChevronRight,
  CircleDashed,
  GripVertical,
} from "lucide-react";
import { useState } from "react";

// Map status tier → visual treatment (Lucide icon + ring colour + Owner-friendly label)
const STATUS_TREATMENT = {
  moving: {
    label: "Moving",
    icon: CheckCircle2,
    ringClass: "ring-2 ring-emerald-400/60 shadow-[0_0_0_4px_rgba(16,185,129,0.10)]",
    iconClass: "text-emerald-600",
    badgeClass: "bg-emerald-50 text-emerald-800 border-emerald-200",
  },
  waiting: {
    label: "Waiting",
    icon: Clock3,
    ringClass: "ring-2 ring-amber-400/60 shadow-[0_0_0_4px_rgba(245,158,11,0.10)]",
    iconClass: "text-amber-600",
    badgeClass: "bg-amber-50 text-amber-800 border-amber-200",
  },
  stuck: {
    label: "Stuck",
    icon: AlertOctagon,
    ringClass: "ring-2 ring-red-400/70 shadow-[0_0_0_5px_rgba(220,38,38,0.12)]",
    iconClass: "text-red-600",
    badgeClass: "bg-red-50 text-red-800 border-red-200",
  },
  parked: {
    label: "Parked",
    icon: PauseCircle,
    ringClass: "ring-1 ring-stone-300",
    iconClass: "text-stone-500",
    badgeClass: "bg-stone-100 text-stone-700 border-stone-200",
  },
};

// A small, accessible swatch grid for the colour picker popover
const COLOR_PALETTE = [
  "#7C3AED", "#F97316", "#0EA5E9", "#14B8A6", "#DC2626",
  "#10B981", "#6366F1", "#EC4899", "#A855F7", "#3B82F6",
  "#F59E0B", "#8B5CF6", "#06B6D4", "#84CC16", "#EF4444",
  "#64748B", "#0F172A", "#92400E",
];

export const SlateCard = ({
  slate,
  editMode,
  dimmed = false,
  isDragging = false,
  isDropTarget = false,
  onColorChange,
  onStatusChange,
  onOpenDetails,
  onClusterEnter,
  onClusterLeave,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd,
}) => {
  const treatment = STATUS_TREATMENT[slate.status_tier] || STATUS_TREATMENT.parked;
  const StatusIcon = treatment.icon;
  const [picking, setPicking] = useState(false);

  const items = Array.isArray(slate.what_to_do_next) ? slate.what_to_do_next : [];
  const hasItems = items.length > 0;
  const clusterId = slate.cluster_id || null;
  const clusterLabel = slate.cluster_label || null;
  const clusterColor = slate.cluster_color || null;

  // Cluster hover handlers — only emit if this slate has a cluster
  const handleMouseEnter = () => {
    if (clusterId && onClusterEnter) onClusterEnter(clusterId);
  };
  const handleMouseLeave = () => {
    if (clusterId && onClusterLeave) onClusterLeave(clusterId);
  };

  // Compose visual modifier classes
  const dimClass = dimmed ? "opacity-40 saturate-50" : "opacity-100";
  const dragClass = isDragging ? "opacity-60 scale-[0.98]" : "";
  const dropTargetClass = isDropTarget
    ? "ring-2 ring-blue-400 shadow-[0_0_0_4px_rgba(59,130,246,0.15)]"
    : "";

  return (
    <Card
      role="listitem"
      data-testid={`slate-card-${slate.id}`}
      data-cluster-id={clusterId || undefined}
      draggable={editMode}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onDragStart={editMode && onDragStart ? (e) => onDragStart(e, slate.id) : undefined}
      onDragOver={editMode && onDragOver ? (e) => onDragOver(e, slate.id) : undefined}
      onDragLeave={editMode && onDragLeave ? (e) => onDragLeave(e, slate.id) : undefined}
      onDrop={editMode && onDrop ? (e) => onDrop(e, slate.id) : undefined}
      onDragEnd={editMode && onDragEnd ? onDragEnd : undefined}
      className={`relative overflow-hidden bg-[hsl(0_0%_99%)] rounded-[18px] aspect-[1/1.414] flex flex-col transition-[transform,opacity,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-lg ${treatment.ringClass} ${dimClass} ${dragClass} ${dropTargetClass} ${editMode ? "cursor-grab active:cursor-grabbing" : ""}`}
    >
      {/* Identity colour strip on the left edge */}
      <div
        className="absolute left-0 top-0 bottom-0 w-[8px]"
        style={{ backgroundColor: slate.identity_color }}
        aria-hidden="true"
      />

      {/* Header */}
      <div className="flex items-start gap-2.5 px-5 pt-5 pb-3 border-b border-stone-200/70">
        {editMode && (
          <span
            className="text-stone-400 hover:text-stone-600 mt-1 shrink-0"
            aria-hidden="true"
            data-testid={`slate-drag-handle-${slate.id}`}
            title="Drag to reorder"
          >
            <GripVertical className="w-4 h-4" />
          </span>
        )}
        {editMode ? (
          <Popover open={picking} onOpenChange={setPicking}>
            <PopoverTrigger asChild>
              <button
                type="button"
                data-testid={`slate-identity-color-button-${slate.id}`}
                className="w-3.5 h-3.5 rounded-full ring-2 ring-white shadow-sm cursor-pointer hover:scale-110 transition-transform mt-1.5 shrink-0"
                style={{ backgroundColor: slate.identity_color }}
                aria-label={`Change identity colour for ${slate.title}`}
              />
            </PopoverTrigger>
            <PopoverContent className="w-[220px] p-3" side="bottom" align="start">
              <p className="text-xs text-stone-500 mb-2 font-medium">Pick a colour for this slate</p>
              <div className="grid grid-cols-6 gap-2 mb-2">
                {COLOR_PALETTE.map((c) => (
                  <button
                    key={c}
                    type="button"
                    className="w-7 h-7 rounded-md ring-1 ring-stone-200 hover:scale-110 transition-transform"
                    style={{ backgroundColor: c }}
                    aria-label={`Use ${c}`}
                    onClick={() => {
                      onColorChange(slate.id, c);
                      setPicking(false);
                    }}
                  />
                ))}
              </div>
              <label className="text-xs text-stone-500">Custom hex</label>
              <input
                type="color"
                data-testid={`slate-identity-color-input-${slate.id}`}
                value={slate.identity_color}
                onChange={(e) => onColorChange(slate.id, e.target.value)}
                className="w-full h-8 mt-1 cursor-pointer rounded"
              />
            </PopoverContent>
          </Popover>
        ) : (
          <span
            className="w-3.5 h-3.5 rounded-full ring-2 ring-white shadow-sm mt-1.5 shrink-0"
            style={{ backgroundColor: slate.identity_color }}
            aria-hidden="true"
          />
        )}
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-[15px] leading-tight text-stone-900 truncate">
            {slate.title}
          </h3>
          <p className="text-[11px] text-stone-500 mt-0.5 line-clamp-2">{slate.subtitle}</p>
          {clusterLabel && (
            <span
              className="inline-flex items-center gap-1 mt-1.5 px-1.5 py-0.5 rounded-full text-[9.5px] font-medium border"
              data-testid={`slate-cluster-chip-${slate.id}`}
              style={{
                color: clusterColor || "#475569",
                borderColor: clusterColor ? `${clusterColor}33` : "#cbd5e1",
                backgroundColor: clusterColor ? `${clusterColor}10` : "#f1f5f9",
              }}
            >
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ backgroundColor: clusterColor || "#64748b" }}
                aria-hidden="true"
              />
              {clusterLabel}
            </span>
          )}
        </div>
        <Badge variant="outline" className={`text-[10px] gap-1 px-1.5 py-0.5 ${treatment.badgeClass}`}>
          <StatusIcon className={`w-3 h-3 ${treatment.iconClass}`} />
          {treatment.label}
        </Badge>
      </div>

      {/* Body — single "What to do next" lane (fix64) */}
      <div className="flex-1 px-5 py-3 overflow-hidden">
        <p className="text-[10px] uppercase tracking-wider text-stone-500 font-medium mb-1.5">
          What to do next
        </p>
        {hasItems ? (
          <ul className="space-y-1.5" role="list" data-testid={`slate-${slate.id}-what-to-do-next`}>
            {items.map((item, i) => (
              <li
                key={i}
                className="text-[12.5px] leading-snug text-stone-800 flex gap-1.5"
                data-testid={`slate-${slate.id}-wtdn-${i}`}
              >
                <span
                  className="mt-0.5 font-semibold text-stone-400"
                  aria-hidden="true"
                >
                  {i + 1}.
                </span>
                <span className="flex-1">{item}</span>
              </li>
            ))}
          </ul>
        ) : slate.status_tier === "stuck" && slate.stuck_reason ? (
          <p className="text-[12px] text-red-700 leading-snug">
            Stuck because: <span className="font-medium">{slate.stuck_reason}</span>
          </p>
        ) : (
          <p className="text-[12px] text-stone-400 italic">No active items yet.</p>
        )}
      </div>

      {/* Edit mode: status override select */}
      {editMode && (
        <div className="px-5 pb-2">
          <Select
            value={slate.status_tier}
            onValueChange={(val) => onStatusChange(slate.id, val)}
          >
            <SelectTrigger
              className="h-7 text-[11px]"
              data-testid={`slate-status-override-select-${slate.id}`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="moving">Moving</SelectItem>
              <SelectItem value="waiting">Waiting</SelectItem>
              <SelectItem value="stuck">Stuck</SelectItem>
              <SelectItem value="parked">Parked</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Footer */}
      <div className="px-5 py-3 border-t border-stone-200/70 bg-stone-50/50 flex items-center justify-between">
        {slate.is_human_dev ? (
          <span className="text-[10px] text-stone-500 flex items-center gap-1">
            <CircleDashed className="w-3 h-3" />
            Developer-owned
          </span>
        ) : (
          <span className="text-[10px] text-stone-500">Auto-updated</span>
        )}
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-[11px] gap-1 hover:bg-stone-100"
          onClick={() => onOpenDetails(slate)}
          data-testid={`slate-open-details-${slate.id}`}
        >
          Open details
          <ChevronRight className="w-3 h-3" />
        </Button>
      </div>
    </Card>
  );
};
